import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Profile weights in kg/m — verified from Excel PROFIL sheet (PWEIGHT g/m ÷ 1000)
const PROFILE_WEIGHTS = {
    // Frame - 2 track
    '04911': 0.784,  // מסילה עליונה/תחתונה ללא הלבשה 2 נת
    '04912': 0.887,  // מסילה תחתונה ללא הלבשה 2 נת (שפרוץ)
    '04957': 1.052,  // מסילה תחתונה עם רשת (for screen variants)
    '04909 / 04910': 0.604, '04909': 0.604, '04910': 0.604, // מזוזה ללא הלבשה
    // Frame - 3 track
    '04924': 1.099,  // מסילה עליונה 3 נתיבים
    '04925': 1.370,  // מסילה תחתונה 3 נתיבים
    '04923': 0.797,  // מזוזה 3 נתיבים
    // Sash - regular glass (4-6mm)
    '04903': 0.483,  // כנף עליון/תחתון
    '04901': 0.542,  // כנף צד
    '04902': 0.439,  // כנף שולב
    // Sash - insulated glass (10-14mm בידודית)
    '04933': 0.468,  // כנף עליון/תחתון לבידודית
    '04931': 0.508,  // כנף צד לבידודית
    '04932': 0.410,  // כנף שולב לבידודית
    // Kiss/Interlock
    '04921': 0.192,  // נושק
    // Trims
    '03358': 0.251,  // הלבשה עגולה 7000
    // Shutter frame
    '04917': 0.815,  // מסילה עליונה 2 נתיבים (לתריס)
    '04914 / 04915': 0.788, '04914': 0.788, '04915': 0.788, // מזוזה מורחקת
    // Fixed section profiles
    '04918': 1.179,  // חציץ אופקי - מעבר לקבוע
    '04962': 1.160,  // חציץ - מעבר לקבוע עליון 2 נתיבים
    '04952': 0.975,  // קבוע עליון/תחתון 2 נתיבים
    '04920': 0.445,  // קבוע צד
    '04707': 0.790,  // חציץ 66 מ"מ
};

const ProfileDisplay = ({ profileStr }) => {
    if (profileStr === '-') return <span>-</span>;
    const profiles = profileStr.split(' / ').map(p => p.trim());

    return (
        <div className="flex gap-3 items-center justify-start">
            {profiles.map(p => (
                <div key={p} className="flex items-center gap-2 bg-white border rounded p-1 shadow-sm print:border-none print:shadow-none">
                    <img
                        src={`/profiles/${p}.jpg`}
                        alt={`פרופיל ${p}`}
                        className="w-10 h-10 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="font-bold text-gray-800 px-1">{p}</span>
                </div>
            ))}
        </div>
    );
};

export default function WindowCalculator() {
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [wings, setWings] = useState(2);
    const [glassType, setGlassType] = useState('10-14');

    // Advanced Inputs
    const [shutter, setShutter] = useState(false);
    const [fixedPart, setFixedPart] = useState('none');
    const [fixedHeight, setFixedHeight] = useState('');
    const [printMode, setPrintMode] = useState('detail'); // 'detail' or 'compact'

    const L = parseFloat(width);
    const H = parseFloat(height);
    const H2 = parseFloat(fixedHeight) || 0;

    // H1 is the height of the sliding window part
    const H1 = fixedPart !== 'none' ? H - H2 : H;

    const getCalculatedData = () => {
        if (!L || !H || (fixedPart !== 'none' && !H2)) return null;

        const pSashTB = glassType === '10-14' ? '04933' : '04903';
        const pSashSide = glassType === '10-14' ? '04931' : '04901';
        const pInterlock = glassType === '10-14' ? '04932' : '04902';

        let frame = [];
        let fixedProfiles = [];
        let sash = [];
        let glassParts = [];
        const trims = [
            { desc: 'הלבשה פנימית - רוחב', profile: '03358', qty: 2, cut: L + 51 },
            { desc: 'הלבשה פנימית - גובה', profile: '03358', qty: 2, cut: H + 51 },
        ];

        if (wings === 2) {
            let topTrack = shutter ? '04917' : '04911';
            let bottomTrack = shutter ? '04912' : '04912'; // 04912 = מסילה תחתונה ללא הלבשה (not 04957 which is screen-only)
            let jamb = shutter ? '04914 / 04915' : '04909 / 04910';
            let trackCut = shutter ? L - 73 : L - 27;  // Excel: F=L-K, K=27 (or K=73 for shutter)
            let sashWCut = shutter ? (L / 2) - 41 : (L / 2) - 17;  // Excel: F=L/2-K, K=17
            let sashHCut = H1 - 68;

            if (fixedPart === 'bottom') {
                frame.push({ desc: 'מסילה עליונה', profile: topTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'חציץ מעבר לקבוע', profile: '04918', qty: 1, cut: L - 27 });
                frame.push({ desc: 'קבוע תחתון', profile: '04952', qty: 1, cut: L - 27 });
                frame.push({ desc: 'מזוזה (צדדים)', profile: jamb, qty: 2, cut: H });

                fixedProfiles.push({ desc: 'קבוע צד', profile: '04920', qty: 2, cut: H2 - 17 });
                fixedProfiles.push({ desc: 'חציץ (עמודון)', profile: '04707', qty: 1, cut: H2 - 17 });
                sashHCut = H1 - 76;
            }
            else if (fixedPart === 'top') {
                frame.push({ desc: 'קבוע עליון', profile: '04952', qty: 1, cut: L - 27 });
                frame.push({ desc: 'חציץ מעבר לקבוע', profile: '04962', qty: 1, cut: L - 27 });
                frame.push({ desc: 'מסילה תחתונה', profile: bottomTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'מזוזה (צדדים)', profile: jamb, qty: 2, cut: H });

                fixedProfiles.push({ desc: 'קבוע צד', profile: '04920', qty: 2, cut: H2 - 17 });
                fixedProfiles.push({ desc: 'חציץ (עמודון)', profile: '04707', qty: 1, cut: H2 - 7 });
                sashHCut = H1 - 98.5;
            }
            else {
                frame.push({ desc: 'מסילה עליונה', profile: topTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'מסילה תחתונה', profile: bottomTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'מזוזה (צדדים)', profile: jamb, qty: 2, cut: H });
            }

            sash.push({ desc: 'כנף עליון/תחתון', profile: pSashTB, qty: 4, cut: sashWCut });
            sash.push({ desc: 'כנף צד', profile: pSashSide, qty: 2, cut: sashHCut });
            sash.push({ desc: 'שולב', profile: pInterlock, qty: 2, cut: sashHCut });

            glassParts.push({ desc: `זכוכית כנף`, qty: 2, text: `${(sashWCut - 35.5).toFixed(1)} x ${(sashHCut - 69).toFixed(1)}` });
            if (fixedPart !== 'none') {
                glassParts.push({ desc: `זכוכית קבוע`, qty: 2, text: `${((L / 2) - 55).toFixed(1)} x ${(H2 - 27).toFixed(1)}` });
            }
        }
        else if (wings === 3) {
            frame = [
                { desc: 'מסילה עליונה (3 נתיבים)', profile: '04924', qty: 1, cut: L - 27 },
                { desc: 'מסילה תחתונה (3 נתיבים)', profile: '04925', qty: 1, cut: L - 27 },
                { desc: 'מזוזה (3 נתיבים)', profile: '04923', qty: 2, cut: H }
            ];
            let sashWCut = (L / 3) - 3;
            sash = [
                { desc: 'כנף עליון/תחתון', profile: pSashTB, qty: 6, cut: sashWCut },
                { desc: 'כנף צד', profile: pSashSide, qty: 2, cut: H1 - 68 },
                { desc: 'שולב', profile: pInterlock, qty: 4, cut: H1 - 68 }
            ];
            glassParts.push({ desc: `זכוכית כנף`, qty: 3, text: `${(sashWCut - 36).toFixed(1)} x ${(H1 - 137).toFixed(1)}` });
        }
        else if (wings === 4) {
            frame = [
                { desc: 'מסילה עליונה', profile: '04911', qty: 1, cut: L - 27 },
                { desc: 'מסילה תחתונה', profile: '04912', qty: 1, cut: L - 27 },
                { desc: 'מזוזה פנימית', profile: '04910', qty: 2, cut: H }
            ];
            let sashWCut = (L / 4) - 10;
            sash = [
                { desc: 'כנף עליון/תחתון', profile: pSashTB, qty: 8, cut: sashWCut },
                { desc: 'כנף צד', profile: pSashSide, qty: 4, cut: H1 - 68 },
                { desc: 'שולב', profile: pInterlock, qty: 4, cut: H1 - 68 },
                { desc: 'נושק לכנף', profile: '04921', qty: 1, cut: H1 - 68 }
            ];
            glassParts.push({ desc: `זכוכית כנף`, qty: 4, text: `${(sashWCut - 35).toFixed(1)} x ${(H1 - 137).toFixed(1)}` });
        }

        let weight = 0;
        [...frame, ...fixedProfiles, ...sash, ...trims].forEach(item => {
            const kgPerMeter = PROFILE_WEIGHTS[item.profile] || 0;
            weight += (item.cut / 1000) * item.qty * kgPerMeter;
        });

        return { frame, fixedProfiles, sash, trims, glassParts, weight: weight.toFixed(2) };
    };

    const data = getCalculatedData();

    return (
        <div className="max-w-5xl mx-auto p-6 bg-gray-50 rounded-xl shadow-lg mt-10 print:shadow-none print:bg-white print:mt-0 print:p-0" dir="rtl">

            <Link to="/professionals" className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 mb-4 transition-colors font-bold print:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                חזרה למחשבונים
            </Link>

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-blue-900 print:text-black">מחשבון ייצור - קליל 1700 בלגי</h2>

                {data && (
                    <div className="flex gap-2 print:hidden">
                        <button
                            onClick={() => { setPrintMode('detail'); setTimeout(() => window.print(), 100); }}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold transition-colors shadow-md text-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            דוח מפורט
                        </button>
                        <button
                            onClick={() => { setPrintMode('compact'); setTimeout(() => window.print(), 100); }}
                            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-bold transition-colors shadow-md text-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            כרטיס ייצור
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-white p-6 rounded-lg shadow-sm border items-end print:hidden">
                <div>
                    <label className="block text-gray-700 font-bold mb-2">רוחב פתח כולל (L) מ"מ:</label>
                    <input type="number" value={width} onChange={(e) => setWidth(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" placeholder="1200" />
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">גובה פתח כולל (H) מ"מ:</label>
                    <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" placeholder="1400" />
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">עובי זכוכית:</label>
                    <select value={glassType} onChange={(e) => setGlassType(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value="10-14">10 עד 14 מ"מ (בידודית)</option>
                        <option value="4-6">4 עד 6 מ"מ (רגילה)</option>
                    </select>
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2">מספר כנפיים:</label>
                    <select value={wings} onChange={(e) => setWings(Number(e.target.value))} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value={2}>2 כנפיים</option>
                        <option value={3}>3 כנפיים</option>
                        <option value={4}>4 כנפיים</option>
                    </select>
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">תריס גלילה?</label>
                    <select value={shutter} onChange={(e) => setShutter(e.target.value === 'true')} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value="false">ללא תריס</option>
                        <option value="true">כולל תריס (מונובלוק)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">קבוע (ללא פתיחה):</label>
                    <select value={fixedPart} onChange={(e) => setFixedPart(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value="none">ללא קבוע</option>
                        <option value="top">קבוע עליון</option>
                        <option value="bottom">קבוע תחתון</option>
                    </select>
                </div>

                {fixedPart !== 'none' && (
                    <div className="md:col-span-3 bg-blue-50 p-4 rounded-lg border border-blue-200 mt-2">
                        <label className="block text-blue-900 font-bold mb-2">גובה הקבוע (H2) במ"מ:</label>
                        <input type="number" value={fixedHeight} onChange={(e) => setFixedHeight(e.target.value)} className="border-2 border-blue-300 p-2 rounded-lg w-1/3 outline-none" placeholder="לדוגמה: 400" />
                        <p className="text-sm text-blue-700 mt-2">גובה הכנפיים (H1) יחושב אוטומטית: {H1 > 0 ? `${H1} מ"מ` : '-'}</p>
                    </div>
                )}
            </div>

            {data && (
                <>
                    {/* ============ DETAILED VIEW ============ */}
                    <div className={`bg-white rounded-lg shadow-sm border overflow-hidden print:border-none print:shadow-none ${printMode === 'compact' ? 'print:hidden' : ''}`}>
                        {/* The Custom Print Header */}
                        <div className="hidden print:block mb-4 pb-4" style={{ borderBottom: '3px solid #1e3a5f' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <img src="/assets/logo-circle.png" alt="Alum Nawatha" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                    <div>
                                        <h1 style={{ fontSize: '24pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0, lineHeight: 1.2 }}>אלום נואטחה בע"מ</h1>
                                        <p style={{ fontSize: '10pt', color: '#555', margin: '2px 0 0 0', letterSpacing: '2px' }}>ALUM NAWATHA LTD</p>
                                        <p style={{ fontSize: '8pt', color: '#888', margin: '2px 0 0 0' }}>מערכות אלומיניום מתקדמות | טל: 052-878-4031</p>
                                    </div>
                                </div>

                                <table style={{ fontSize: '9pt', borderCollapse: 'collapse', textAlign: 'right', direction: 'rtl' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>תאריך:</td>
                                            <td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{new Date().toLocaleDateString('he-IL')}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>מידות פתח:</td>
                                            <td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{L} × {H} מ"מ</td>
                                        </tr>
                                        {fixedPart !== 'none' && (
                                            <tr>
                                                <td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>חלוקת גובה:</td>
                                                <td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>קבוע: {H2} | כנף: {H1}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>מפרט:</td>
                                            <td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{wings} כנפיים {shutter && '| מותאם לתריס'}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333' }}>זכוכית:</td>
                                            <td style={{ padding: '3px 12px', color: '#555' }}>{glassType} מ"מ</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13pt', fontWeight: 'bold', color: '#1e3a5f' }}>
                                הוראת ייצור — חלון הזזה קליל 1700 בלגי
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-right border-collapse">
                                <thead>
                                    <tr className="bg-blue-100 border-b-2 border-blue-200 text-blue-900 print:bg-gray-100 print:border-gray-400 print:text-black">
                                        <th className="p-3 text-lg border-l print:border-gray-300">תיאור</th>
                                        <th className="p-3 text-lg border-l print:border-gray-300">פרופיל</th>
                                        <th className="p-3 text-lg border-l print:border-gray-300">כמות</th>
                                        <th className="p-3 text-lg">מידת חיתוך (מ"מ)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">מסגרת (משקוף)</td></tr>
                                    {data.frame.map((row, i) => (
                                        <tr key={`fr-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300">
                                            <td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td>
                                            <td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td>
                                        </tr>
                                    ))}

                                    {data.fixedProfiles.length > 0 && (
                                        <>
                                            <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">מסגרת לקבוע</td></tr>
                                            {data.fixedProfiles.map((row, i) => (
                                                <tr key={`f-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300">
                                                    <td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td>
                                                    <td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td>
                                                </tr>
                                            ))}
                                        </>
                                    )}

                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">כנפיים</td></tr>
                                    {data.sash.map((row, i) => (
                                        <tr key={`s-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300">
                                            <td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td>
                                            <td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td>
                                        </tr>
                                    ))}

                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">הלבשות</td></tr>
                                    {data.trims.map((row, i) => (
                                        <tr key={`t-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300">
                                            <td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td>
                                            <td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td>
                                        </tr>
                                    ))}

                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">זיגוג ({glassType} מ"מ)</td></tr>
                                    {data.glassParts.map((row, i) => (
                                        <tr key={`g-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300">
                                            <td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr="-" /></td>
                                            <td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-blue-600 font-bold print:text-black">{row.text}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-gray-800 text-white p-4 flex justify-between items-center rounded-b-lg print:bg-gray-100 print:text-black print:border-t-2 print:border-gray-400 print:mt-4">
                            <h3 className="text-lg font-bold">סה"כ משקל אלומיניום מוערך:</h3>
                            <div className="text-2xl font-mono font-bold text-yellow-400 print:text-black">
                                {data.weight} <span className="text-sm text-gray-300 print:text-black">ק"ג</span>
                            </div>
                        </div>
                    </div>

                    {/* ============ COMPACT VIEW (Production Card) ============ */}
                    <div className={`hidden ${printMode === 'compact' ? 'print:block' : ''}`}>
                        {/* Print Header */}
                        <div className="mb-4 pb-3" style={{ borderBottom: '3px solid #1e3a5f' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <img src="/assets/logo-circle.png" alt="Alum Nawatha" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                                    <div>
                                        <h1 style={{ fontSize: '16pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0 }}>אלום נואטחה בע"מ</h1>
                                        <p style={{ fontSize: '8pt', color: '#888', margin: 0 }}>ALUM NAWATHA LTD | טל: 052-878-4031</p>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'left', fontSize: '9pt', color: '#555' }}>
                                    <div><b>תאריך:</b> {new Date().toLocaleDateString('he-IL')}</div>
                                    <div><b>מפרט:</b> {wings} כנפיים {shutter ? '+ תריס' : ''}</div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12pt', fontWeight: 'bold', color: '#1e3a5f' }}>
                                כרטיס ייצור — קליל 1700 בלגי
                            </div>
                        </div>

                        {/* Compact 3-Column Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', fontSize: '10pt' }}>
                            <thead>
                                <tr style={{ background: '#e8f0fe' }}>
                                    <th style={{ border: '2px solid #1e3a5f', padding: '8px', width: '33%', fontSize: '12pt', color: '#1e3a5f' }}>מידות פתח</th>
                                    <th style={{ border: '2px solid #1e3a5f', padding: '8px', width: '34%', fontSize: '12pt', color: '#1e3a5f' }}>מסגרת</th>
                                    <th style={{ border: '2px solid #1e3a5f', padding: '8px', width: '33%', fontSize: '12pt', color: '#1e3a5f' }}>כנפיים</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ verticalAlign: 'top' }}>
                                    {/* RIGHT - Input Dimensions */}
                                    <td style={{ border: '2px solid #1e3a5f', padding: '10px' }}>
                                        <div style={{ fontSize: '13pt', fontWeight: 'bold', textAlign: 'center', marginBottom: '8px', color: '#c00' }}>
                                            {L} × {H}
                                        </div>
                                        <div style={{ fontSize: '9pt', color: '#555', textAlign: 'center' }}>רוחב × גובה (מ"מ)</div>
                                        {fixedPart !== 'none' && (
                                            <div style={{ marginTop: '6px', padding: '4px', background: '#f5f5f5', borderRadius: '4px', fontSize: '9pt', textAlign: 'center' }}>
                                                <b>קבוע {fixedPart === 'top' ? 'עליון' : 'תחתון'}:</b> {H2}<br />
                                                <b>כנף:</b> {H1}
                                            </div>
                                        )}
                                        <div style={{ marginTop: '6px', fontSize: '9pt', textAlign: 'center' }}>
                                            <b>זכוכית:</b> {glassType} מ"מ
                                        </div>
                                        {data.glassParts.map((g, i) => (
                                            <div key={i} style={{ marginTop: '4px', padding: '4px', background: '#eef6ff', borderRadius: '4px', fontSize: '9pt', textAlign: 'center' }}>
                                                <b>{g.desc}:</b> {g.text}
                                            </div>
                                        ))}
                                    </td>

                                    {/* MIDDLE - Frame Cuts */}
                                    <td style={{ border: '2px solid #1e3a5f', padding: '10px' }}>
                                        {data.frame.map((row, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                            </div>
                                        ))}
                                        {data.fixedProfiles.length > 0 && (
                                            <>
                                                <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1e3a5f', marginTop: '6px', borderBottom: '1px solid #ccc', paddingBottom: '2px' }}>קבוע:</div>
                                                {data.fixedProfiles.map((row, i) => (
                                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                        <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                        <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                        <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1e3a5f', marginTop: '6px', borderBottom: '1px solid #ccc', paddingBottom: '2px' }}>הלבשות:</div>
                                        {data.trims.map((row, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                            </div>
                                        ))}
                                    </td>

                                    {/* LEFT - Wing/Sash Cuts */}
                                    <td style={{ border: '2px solid #1e3a5f', padding: '10px' }}>
                                        {data.sash.map((row, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                            </div>
                                        ))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Weight footer */}
                        <div style={{ marginTop: '10px', padding: '8px', background: '#f3f3f3', textAlign: 'center', fontWeight: 'bold', fontSize: '11pt', borderRadius: '4px', border: '1px solid #ddd' }}>
                            משקל אלומיניום מוערך: {data.weight} ק"ג
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}